import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress } from "@mui/material";
import { Globe } from "lucide-react";

import colors from "../../utils/colors";
import useSiteScope from "../../hooks/user/useSiteScope";
import { PrimaryButton } from "./Button";
import LayerdIcon from "./LayerdIcon";

function SiteScopeMessage({ status }) {
  const navigate = useNavigate();

  if (status === "loading") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={32} sx={{ color: colors.primary[500] }} />
      </Box>
    );
  }

  const isNoSites = status === "no_sites";
  const title = isNoSites ? "No sites configured" : "No site selected";
  const body = isNoSites
    ? "Add a site to this project to view this page."
    : "Select a site from the header dropdown to view this page.";

  return (
    <Box
      sx={{
        borderRadius: "8px",
        border: `solid 1px ${colors.neutral[200]}`,
        backgroundColor: "white",
        p: 6,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          py: 4,
        }}
      >
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "8px",
            backgroundColor: colors.neutral[100],
            color: colors.neutral[600],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
            border: `1px solid ${colors.neutral[200]}`,
          }}
        >
          <LayerdIcon icon={Globe} size="md" color="neutral" />
        </Box>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 500,
            color: colors.neutral[1000],
            mb: 1,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            color: colors.neutral[700],
            maxWidth: 400,
            lineHeight: "21px",
          }}
        >
          {body}
        </Typography>
        {isNoSites && (
          <PrimaryButton
            sx={{ mt: 3 }}
            onClick={() => navigate("/v2/sites/new")}
          >
            Add a site
          </PrimaryButton>
        )}
      </Box>
    </Box>
  );
}

/**
 * Render-prop gate for pages that depend on site scope.
 * children(siteUid, ctx) runs only when status === 'ready'.
 */
export default function SiteScopeGate({
  mode = "single",
  autoPickSite = true,
  children,
}) {
  const scope = useSiteScope({ mode, autoPickSite });

  if (scope.status !== "ready") {
    return <SiteScopeMessage status={scope.status} />;
  }

  return children(scope.siteUid, scope);
}

export { SiteScopeMessage };
